import styled, { css } from 'styled-components/macro';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { profileActions } from 'src/store/profile/profile.actions';
import { getProfile } from 'src/store/profile/profile.selectors';
import { Form, Choice } from '@rocketseat/unform';
import { InputField } from 'src/components/Input';
import Button from 'src/components/Button';
import { Header } from 'src/components/Header';
import * as Yup from 'yup';
import { InfoBubble } from 'src/components/InfoBubble';
import { withRouter } from 'react-router';
import {
  getBrsvDisplay,
  getKsvStatusDisplay,
  getFormattedAnimalKeepingInfo,
  getFormattedAnimalRestrictionsInfo,
} from 'src/routes/Profile/Profile';
import { useToast } from 'src/components/Toast';
import Helptext from 'src/components/Helptext';
import { getBrandingColor, colorTypes } from 'src/branding';
import { Breadcrumbs } from 'src/components/Breadcrumbs';
import { routes } from '../routes.manifest';
import {
  Page, Label, LinkLabel, Column,
} from '../../components/Layout';

const desktopViewBreakpoint = '620px';

const FieldNameLabel = styled(Label)`
  display: inline-block;
  width: 33%;
  height: 44px;
  line-height: 44px;

  @media screen and (max-width: 700px) {
    width: 25%;
  }
  @media screen and (max-width: ${desktopViewBreakpoint}) {
    font-weight: 600;
    width: 100%;
    height: 19px;
    line-height: 19px;
  }
`;

const FieldText = styled(Label)`
  display: inline-block;
  height: 44px;
  line-height: 44px;
`;

const PostNoInput = styled(InputField)``;
const AddressFields = styled.div`
  margin-left: 33%;
  @media screen and (max-width: 700px) {
    margin-left: 25%;
  }

  @media screen and (max-width: ${desktopViewBreakpoint}) {
    margin-left: 0;
  }
`;
const Wrapper = styled.div`
  flex: none;
`;

const FormRow = styled.div`
  @media screen and (max-width: 500px) {
    input[name='city'] {
      margin-top: 8px;
    }
  }
`;

const MultilineFormRow = styled(FormRow)`
  display: flex;

  @media screen and (max-width: ${desktopViewBreakpoint}) {
    display: block;
  }
`;

const StyledForm = styled(Form)`
  flex-direction: column;
  width: 100%;
  max-width: 710px;
  margin-left: auto;
  margin-right: auto;

  input {
    width: 280px;
  }

  @media screen and (max-width: ${desktopViewBreakpoint}) {
    /*Error messages for input-field*/
    input {
      margin-bottom: 5px;
    }
    input + span {
      display: block;
      margin-left: 0;
      margin-bottom: 7px;
      position: relative;
      left: 2px;
      bottom: 2px;
    }

    button {
      padding: 7px 25px;
    }
  }

  @media screen and (max-width: 600px) {
    width: 100%;
    input {
      width: 100%;
      min-width: 200px;
    }
  }
  /*
  /* Styling of checkbox mark https://stackoverflow.com/a/48718254 */
  input[type='checkbox']:checked + label::after {
    content: '';
    position: absolute;
    width: 1.2ex;
    height: 0.4ex;
    background: rgba(0, 0, 0, 0);
    top: 50%;
    left: 0.4ex;
    border: 0.4ex solid ${getBrandingColor(colorTypes.primary)};
    border-top: none;
    border-right: none;
    -webkit-transform: translateY(-50%) rotate(-45deg);
    -moz-transform: translateY(-50%) rotate(-45deg);
    -o-transform: translateY(-50%) rotate(-45deg);
    -ms-transform: translateY(-50%) rotate(-45deg);
    transform: translateY(-50%) rotate(-45deg);
  }

  input[type='checkbox'] {
    line-height: 2.1ex;
  }

  input[type='checkbox']:focus + label::before {
    border: 1px solid gray;
  }

  input[type='radio'],
  input[type='checkbox'] {
    position: absolute;
    left: -999em;
  }

  input[type='checkbox'] + label {
    position: relative;
    overflow: hidden;
    cursor: pointer;
  }

  input[type='checkbox'] + label::before {
    content: '';
    display: inline-block;
    vertical-align: -25%;
    height: 2.4ex;
    width: 2.4ex;
    background-color: ${getBrandingColor(colorTypes.whiteAltBg)};
    border: 1.115px solid rgba(78, 122, 185, 0.22);
    border-radius: 4px;
    margin-right: 0.5em;
    margin-top: 0.5em;
    box-sizing: border-box;
  }
`;

const EditProfileHeader = styled(Header)`
  @media screen and (min-width: 700px) {
    text-align: center;
  }
`;

// custom styling for checkbox is inside StyledForm
const StyledChoice = styled(Choice)`
  margin-bottom: 10px;
`;

export const EditProfile = withRouter(({ history }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const profileSelector = useSelector(getProfile);

  const getValueFromCheckbox = (checkboxSubmission) => (checkboxSubmission.length && checkboxSubmission[0] === '1' ? true : false);

  const onSubmit = (form) => {
    dispatch(
      profileActions.save({
        profile: {
          ...profileSelector.profile,
          ...form,
          recieve_accounting: getValueFromCheckbox(form.recieve_accounting),
          recieve_newsletter: getValueFromCheckbox(form.recieve_newsletter),
          recieve_registered_slaughter: getValueFromCheckbox(
            form.recieve_registered_slaughter,
          ),
        },
        onSuccess: () => {
          toast.addMessage('Profilen ble oppdatert');
          history.replace({
            pathname: routes.profile,
            state: { referer: routes.profileEdit },
          });
        },
        onError: () => {
          toast.addError('Kunne ikke oppdatere profilen');
        },
      }),
    );
  };

  useEffect(() => {
    if (
      !profileSelector.profile
      && !profileSelector.isFetching
      && !profileSelector.error
    ) {
      dispatch(profileActions.fetch());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const formSchema = Yup.object().shape({
    email_user: Yup.string()
      .matches(/^[^@]+@[^@.]+\.[^@]+$/, { message: 'Ugyldig e-post' })
      .required('Feltet må fylles ut'),
    email_accounting: Yup.string()
      .matches(/^[^@]+@[^@.]+\.[^@]+$/, { message: 'Ugyldig e-post' })
      .required('Feltet må fylles ut'),
    mobile: Yup.string()
      .matches(/^[0-9\+\- ]{5,}$/, { message: 'Ugyldig telefonnummer' })
      .required('Feltet må fylles ut'),
    street_address: Yup.string()
      .matches(/^.*[^0-9 ]+.*$/, { message: 'Ugyldig adresse' })
      .required('Feltet må fylles ut'),
    post_no: Yup.string()
      .matches(/^[0-9]{4}$/, { message: 'Ugyldig postnummer' })
      .required('Feltet må fylles ut'),
    city: Yup.string().required('Feltet må fylles ut'),

    /* The checkboxes has this: ['1'] value when checked and [] when unckecked */
    recieve_accounting: Yup.array().nullable(),
    recieve_newsletter: Yup.array().nullable(),
    recieve_registered_slaughter: Yup.array().nullable(),
  });

  const profile = profileSelector.profile;
  const loadingData = profileSelector.isFetching || !profileSelector.profile;
  return (
    <Page title="Endre profil" style={{ padding: '1em' }}>
      <Wrapper>
        <Breadcrumbs
          breadcrumbs={[{ title: 'Profil', url: '/profile' }, { title: 'Rediger', url: '/profile/edit' }]}
          wrapperStyle={css`
            margin-bottom: -1.3rem;
            width: fit-content;
            margin-left: auto;
            margin-right: auto;
            @media (max-width:  700px){
              padding-left: 1em;
              margin-left: 0;
              margin-right: 0;
            }
            `}
        />
        <EditProfileHeader style={{ marginTop: '2em' }}>
          Endre profil
        </EditProfileHeader>
        {(profileSelector.error && <h2>Kunne ikke laste inn profildata.</h2>)
          || (loadingData && <h2>Laster profildata ...</h2>) || (
            <Column
              style={{
                maxWidth: '720px',
                width: '100%',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              <StyledForm
                onSubmit={onSubmit}
                schema={formSchema}
                initialData={{
                  street_address: profile.street_address,
                  city: profile.city,
                  email_user: profile.email_user,
                  email_accounting: profile.email_accounting,
                  mobile: profile.mobile,
                  post_no: profile.post_no,
                  recieve_registered_slaughter: profile.recieve_registered_slaughter
                    ? '1'
                    : '0',
                  recieve_newsletter: profile.recieve_newsletter ? '1' : '0',
                  recieve_accounting: profile.recieve_accounting ? '1' : '0',
                }}
              >
                <FormRow>
                  <FieldNameLabel>Navn: </FieldNameLabel>
                  <FieldText style={{ minWidth: '1px' }}>
                    {profile.fullname}
                  </FieldText>
                  <Helptext
                    name="edit-name"
                    render={(text) => text && (
                      <InfoBubble
                        ariaLabel="Informasjon on feltet"
                        popupStyle={{
                          maxWidth: '100px',
                        }}
                        style={{
                          position: 'absolute',
                          marginLeft: '4px',
                          marginTop: '-1px',
                        }}
                      >
                        <p
                          style={{
                            marginTop: '7px',
                            marginBottom: '15px',
                            fontSize: '0.9rem',
                          }}
                        >
                          {text}
                        </p>
                      </InfoBubble>
                    )}
                  />
                </FormRow>
                <FormRow>
                  <FieldNameLabel>Leverandørnr: </FieldNameLabel>
                  <FieldText>{profile.supplier_no}</FieldText>
                  <Helptext
                    name="edit-provider-no"
                    render={(text) => text && (
                      <InfoBubble
                        ariaLabel="Informasjon om feltet"
                        popupStyle={{
                          maxWidth: '100px',
                        }}
                        style={{
                          position: 'absolute',
                          marginLeft: '4px',
                          marginTop: '-1px',
                        }}
                      >
                        <p
                          style={{
                            marginTop: '7px',
                            marginBottom: '15px',
                            fontSize: '0.9rem',
                          }}
                        >
                          {text}
                        </p>
                      </InfoBubble>
                    )}
                  />
                </FormRow>
                <FormRow>
                  <FieldNameLabel>Foretaksnr: </FieldNameLabel>
                  <FieldText style={{ minWidth: '1px' }}>
                    {profile.company_no}
                  </FieldText>
                  <Helptext
                    name="edit-company-no"
                    render={(text) => text && (
                      <InfoBubble
                        ariaLabel="Informasjon om feltet"
                        popupStyle={{
                          maxWidth: '100px',
                        }}
                        style={{
                          position: 'absolute',
                          marginLeft: '4px',
                          marginTop: '-1px',
                        }}
                      >
                        <p
                          style={{
                            marginTop: '7px',
                            marginBottom: '15px',
                            fontSize: '0.9rem',
                          }}
                        >
                          {text}
                        </p>
                      </InfoBubble>
                    )}
                  />
                </FormRow>
                <FormRow>
                  <FieldNameLabel>Produsentnr: </FieldNameLabel>
                  <FieldText>{profile.provider_no}</FieldText>
                </FormRow>
                <FormRow>
                  <FieldNameLabel>DyreholdsID: </FieldNameLabel>
                  <FieldText>{profile.animal_keeping_id}</FieldText>
                </FormRow>

                <FormRow>
                  <FieldNameLabel>KSL-status: </FieldNameLabel>
                  <FieldText>{getKsvStatusDisplay(profile)}</FieldText>
                </FormRow>

                <FormRow>
                  <FieldNameLabel>BRSV/BCoV-status: </FieldNameLabel>
                  <FieldText>{getBrsvDisplay(profile)}</FieldText>
                </FormRow>

                {profile.animal_keeping && (
                  <div>
                    <MultilineFormRow>
                      <FieldNameLabel>Dyrehold: </FieldNameLabel>
                      <FieldText>
                        {getFormattedAnimalKeepingInfo(profile.animal_keeping)}
                      </FieldText>
                    </MultilineFormRow>

                    <MultilineFormRow style={{ marginTop: '0.6em' }}>
                      <FieldNameLabel>Restriksjoner: </FieldNameLabel>
                      <FieldText>
                        {getFormattedAnimalRestrictionsInfo(
                          profile.animal_keeping,
                        )}
                      </FieldText>
                    </MultilineFormRow>
                  </div>
                )}

                <FormRow>
                  <FieldNameLabel>E-post til leverandør:</FieldNameLabel>
                  <InputField inline name="email_user" />
                </FormRow>
                <FormRow>
                  <FieldNameLabel>E-post til regnskap:</FieldNameLabel>
                  <InputField inline name="email_accounting" />
                </FormRow>
                <FormRow>
                  <FieldNameLabel>Mobil:</FieldNameLabel>
                  <InputField
                    inline
                    nomargin
                    name="mobile"
                    type="tel"
                  />
                </FormRow>
                <FormRow>
                  <FieldNameLabel style={{ float: 'left' }}>
                    Adresse:
                  </FieldNameLabel>
                  <AddressFields>
                    <InputField
                      name="street_address"
                      style={{ marginTop: '4px' }}
                      inline
                      nomargin
                      placeholder="Gateadresse"
                    />

                    <FormRow>
                      <PostNoInput
                        name="post_no"
                        type="number"
                        min="0"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        style={{ width: '87px', marginRight: '12px' }}
                        placeholder="Postno"
                        inline
                      />
                      <InputField
                        name="city"
                        placeholder="By"
                        inline
                        nomargin
                      />
                    </FormRow>

                  </AddressFields>
                </FormRow>
                <LinkLabel
                  to={routes.changePw}
                  style={{
                    textAlign: 'center',
                    marginTop: '1.3em',
                    marginBottom: '1.4em',
                  }}
                >
                  Endre passord
                </LinkLabel>

                <div>
                  <StyledChoice
                    name="recieve_newsletter"
                    id="recieve_newsletter"
                    multiple
                    options={[
                      {
                        value: '1',
                        label: 'Motta nyhetsbrev',
                      },
                    ]}
                  />
                </div>
                <div>
                  <StyledChoice
                    name="recieve_accounting"
                    id="recieve_accounting"
                    value="checked"
                    multiple
                    options={[
                      { value: '1', label: 'Motta avregning på e-post' },
                    ]}
                  />
                </div>

                <div>
                  <StyledChoice
                    name="recieve_registered_slaughter"
                    id="recieve_registered_slaughter"
                    value="checked"
                    multiple
                    options={[
                      {
                        value: '1',
                        label: 'Motta epost ved innmelding',
                      },
                    ]}
                  />
                </div>

                <div style={{ marginTop: '3em' }}>
                  <Button
                    secondary
                    type="button"
                    onClick={() => history.goBack()}
                    style={{
                      display: 'inline-block',
                      width: 'fit-content',
                      marginRight: '1em',
                    }}
                  >
                    Avbryt
                  </Button>
                  <Button
                    primary
                    type="submit"
                    style={{
                      width: 'fit-content',
                      marginTop: '0.8em',
                    }}
                  >
                    Utfør endring
                  </Button>
                </div>
              </StyledForm>
            </Column>
        )}
      </Wrapper>
    </Page>
  );
});
